import { defineComponent, isRef, type FunctionalComponent, type CSSProperties, computed, unref } from 'vue'
import { SUBFORM_PROPS, type SubFormProps } from '../types'
import { isFieldRef } from '../formv2/hooks/useForm/utils'
import type { IForm } from '../types/form'
import type { FormFieldRef, TFormFieldDict } from '../types/field'
import { Grid } from 'components/grid'
import { useFormFieldRender } from '../render'
import { FormLayout } from '../form-layout'

const SubForm = defineComponent<SubFormProps>({
  name: 'FormComponentV3',
  setup(props) {
    const schema = props.schema
    const depth = props.depth || 0
    const Wrapper = schema.$grid
      ? (((_, ctx) => {
          return (
            <Grid {...schema.$grid} {...(schema.$injectedProps || {})}>
              {ctx.slots.default ? ctx.slots.default() : ''}
            </Grid>
          )
        }) satisfies FunctionalComponent<any>)
      : (schema.$wrapper as unknown as FunctionalComponent<any>)

    const renderer = useFormFieldRender({
      handler: props.handler,
      schema: schema
    });

    const renderField = ([key, field]: [string, FormFieldRef]) => {
      if (!field) return <div>{key} is null or undefined</div>;
      let block: FormFieldRef | undefined = undefined
      const ff = field

      if (ff && !isRef(ff) && (ff as any).$ref) {
        block = (ff as any).$ref as FormFieldRef
      }

      let hidden = Boolean(field.value && field.value.hidden)
      if (block && block.value.hidden) {
        hidden = block.value.hidden
      }

      const style: CSSProperties = hidden
        ? {
            display: 'none',
            opacity: '0%'
          }
        : {}

      const fieldDef = props.schema[key as any] as IForm<any>
      if (fieldDef && isFieldRef(field)) {
        return renderer.renderField(key, field);
      }

      return depth > 80 ? (
        <div class="bg-red-600 text-red-200">ERROR</div>
      ) : (
        <SubForm
          handler={props.handler}
          style={style}
          name={key}
          key={key}
          schema={(schema[key] || {}) as IForm<any>}
          fields={((props.fields as any) || {})[key] as TFormFieldDict<any>}
          depth={depth + 1}
          onStateChange={props.onStateChange}
          reportChange={props.reportChange}
          triggerBlur={props.triggerBlur || props.handler.triggerBlur}
          outputState={props.outputState}
          outputStates={props.outputStates}
          outputStats={props.outputStats}
          componentExtensions={props.componentExtensions}
        />
      )
    }

    const formFields = computed(() => {
      if (props.fieldArray && props.fieldArray.length > 0) return props.fieldArray
      if (!props.fields) return []
      return Object.entries(props.fields).filter(([k]) => !k.startsWith('$'))
    })

    const WrapperComp: FunctionalComponent = (_, ctx) => {
      const layoutClass =
        schema.$layoutClass ||
        (schema.$layout === 'horizontal' ? 'grid grid-cols-2' : 'grid grid-rows-auto') + ' gap-4'
      return Wrapper ? (
        <Wrapper>{ctx.slots.default ? ctx.slots.default() : ''}</Wrapper>
      ) : (
        <div class={[layoutClass, schema.$wrapperClass || '']} {...(schema.$injectedProps || {})}>
          {ctx.slots.default ? ctx.slots.default() : ''}
        </div>
      )
    }

    const H = props.handler;
    const render = () => {
      const layout = unref(H.layout);
      if (layout && layout.length > 0) {
        return layout.map((child) => <FormLayout key={`layout-${unref(child)?.$label}`} layout={child} handler={props.handler}/>)
      }
      return formFields.value.map(renderField as any)
    }

    return () => {
      return (
        <WrapperComp>
          {schema.$label && (
            <div class="border-b border-gray-200 pb-2 mt-2 font-semibold">{schema.$label}</div>
          )}
          {render()}
        </WrapperComp>
      )
    }
  }
})

SubForm.props = SUBFORM_PROPS

export default SubForm
